import React, { Suspense, useCallback, useEffect } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import type { EmojiProvider } from '@atlaskit/emoji';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { AudioFormat } from '../../../api/cc-smarts/getAudio';
import { AUDIO_CONTAINER_MODES } from '../../../types/audioContainerMode';
import type { AudioFeature } from '../../../types/audioFeature';
import { AudioPlayerStreamingProvider } from '../../context/AudioPlayerStreamingProvider';
import { EmojiProviderContext } from '../../context/EmojiProviderContext';
import { useAudioPlaybackSync } from '../../hooks/useAudioPlaybackSync';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreaming } from '../../hooks/useAudioPlayerStreaming';
import { useMouseOverWithDelayedExit } from '../../hooks/useMouseOverWithDelayedExit';

import type { AudioContainerQuery } from './__generated__/AudioContainerQuery.graphql';
import { AutoPlayManager } from './AutoPlayManager';
import { ExpandedAudioPlayer } from './ExpandedAudioPlayer';
import { MinifiedAudioPlayer } from './MinifiedAudioPlayer';

// Minimal interface for flags that works with both Atlaskit and Confluence flags
// Confluence's showFlag returns Promise<FlagDescriptor>, Atlaskit's returns DismissFn
export type FlagsContainer = {
	showFlag: (flag: Record<string, unknown>) => unknown;
};

const styles = cssMap({
	container: {
		borderRadius: token('radius.xlarge'),
		position: 'relative',
		display: 'inline-block',
		verticalAlign: 'top',
		overflow: 'hidden',
	},
});

type AudioContainerProps = {
	product: string;
	cloudId: string;
	activationId: string;
	audioFeatures: AudioFeature[];
	emojiProvider: EmojiProvider;
	flags: FlagsContainer;
	pinToExpandedView?: boolean;
};

export const AudioContainer = ({
	product,
	cloudId,
	activationId,
	audioFeatures,
	emojiProvider,
	flags,
	pinToExpandedView = false,
}: AudioContainerProps): React.JSX.Element => {
	const {
		activeAudioContainerMode,
		activeFeature,
		activeContent,
		pendingContent,
		setCustomization,
		setActiveAudioContainerMode,
	} = useAudioPlayerStore();

	const handleMouseOverChange = useCallback(
		(isHovered: boolean) =>
			setActiveAudioContainerMode(
				isHovered ? AUDIO_CONTAINER_MODES.EXPANDED : AUDIO_CONTAINER_MODES.MINIFIED,
			),
		[setActiveAudioContainerMode],
	);

	const { isMouseOver, handleMouseEnter, handleMouseLeave } = useMouseOverWithDelayedExit({
		delay: 3000,
		initiallyHovered: true,
		resetKey: pendingContent?.contentId,
		isPinned: pinToExpandedView,
		onMouseOverChange: handleMouseOverChange,
	});

	const { updatePlayerState } = useAudioPlayerStore();

	useEffect(() => {
		updatePlayerState({
			product,
			cloudId,
			activationId,
		});
	}, [product, cloudId, activationId, updatePlayerState]);

	const { audioRef, fetchAudio } = useAudioPlayerStreaming({
		locale: 'en-US',
		type: activeFeature,
		format: AudioFormat.MP3,
		product,
		cloudId,
		activationId,
	});

	useAudioPlaybackSync({ audioRef });

	const environment = useRelayEnvironment();

	useEffect(() => {
		const fetchAudioPreference = async () => {
			try {
				const data = await fetchQuery<AudioContainerQuery>(
					environment,
					graphql`
						query AudioContainerQuery($cloudId: ID!) {
							confluence_getAudioPreference(cloudId: $cloudId) {
								tone
								length
								playbackSpeed
							}
						}
					`,
					{ cloudId },
				).toPromise();

				if (data) {
					const tone = data?.confluence_getAudioPreference?.tone;
					const length = data?.confluence_getAudioPreference?.length;
					const playbackSpeed = data?.confluence_getAudioPreference?.playbackSpeed;

					if (tone && length && playbackSpeed) {
						setCustomization({
							tone: tone,
							length: length,
							playbackSpeed: playbackSpeed,
						});
						if (audioRef.current) {
							audioRef.current.defaultPlaybackRate = playbackSpeed;
						}
					} else {
						// TODO: CCI-13829 Handle error appropriately
					}
				}
			} catch {
				// TODO: CCI-13829 Handle error appropriately
			}
		};

		void fetchAudioPreference();
	}, [audioRef, environment, cloudId, setCustomization]);

	return (
		<AudioPlayerStreamingProvider fetchAudio={fetchAudio} audioRef={audioRef}>
			{/* Enable auto-play of next track when current track ends */}
			<Suspense fallback={null}>
				<AutoPlayManager cloudId={cloudId} />
			</Suspense>
			<EmojiProviderContext.Provider value={{ emojiProvider }}>
				<Box
					xcss={styles.container}
					style={{ width: `${activeAudioContainerMode.width}px` }}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					testId="audio-container"
				>
					{isMouseOver ? (
						<ExpandedAudioPlayer audioFeatures={audioFeatures} flags={flags} />
					) : (
						<MinifiedAudioPlayer
							title={activeContent.title || 'Untitled'}
							byline={activeContent.author || ''}
							avatarUrl={activeContent.avatarUrl || ''}
						/>
					)}
					<audio ref={audioRef} />
				</Box>
			</EmojiProviderContext.Provider>
		</AudioPlayerStreamingProvider>
	);
};
